import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  CheckCircleIcon,
  TrophyIcon,
  NoSymbolIcon,
  ArrowsRightLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { weeklyProgramsAPI } from '../../services/api'
import { useFieldTemplates } from '../../hooks/useFieldTemplates'
import { isWeightBasedModule, fieldsFromCustomMetrics } from './workoutHelpers'
import { useAvgSpeedProposal } from '../../utils/avgSpeed'
import MachineChoice from '../capture/MachineChoice'

// VH-206: the server's 422 detail (a machine that is not a candidate here) is
// worth showing verbatim — it says which rule was broken.
function errorDetail(error) {
  const detail = error?.response?.data?.detail
  return error?.response?.status === 422 && typeof detail === 'string' ? detail : null
}

export function ExerciseCard({ exercise, program, session, moduleType, sessionData, onSaveSession, onSubstitute }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const { getTemplate, getFieldLabel } = useFieldTemplates()

  const isEditable = program.status !== 'submitted' && !session?.is_completed
  const exerciseType = exercise.snapshot_exercise_type || moduleType || 'strength'
  const template = getTemplate(exerciseType)

  // VH-153: the exercise's OWN configured fields win over the generic per-type
  // template — the rule VH-139 established for the capture sheet, which this
  // card never received. Reto's "Walking / Hiking" is configured with duration,
  // distance, intensity and steps; the cardio template defines exactly duration
  // and distance, so the card showed two of his four fields with no way to
  // reach the others.
  //
  // Not merged with the template fields: VH-139 found merging shows the same
  // quantity twice under two names (`duration` beside `duration_min`).
  //
  // Resolved ONCE here because four things consume it — the initial seed, the
  // re-seed effect, the avg-speed proposal and the render. Reading
  // `template.fields` directly in any of them would silently reintroduce the
  // bug for that one path.
  // useMemo, not a bare expression: this feeds a useEffect dependency, and a
  // fresh array every render would re-seed the inputs on every keystroke.
  const resolvedFields = useMemo(() => {
    const configured = fieldsFromCustomMetrics(exercise.snapshot_custom_metrics)
    return configured.length > 0 ? configured : (template?.fields || [])
  }, [exercise.snapshot_custom_metrics, template?.fields])

  const hasConfiguredFields =
    fieldsFromCustomMetrics(exercise.snapshot_custom_metrics).length > 0

  // Configured fields imply a non-set sheet: they describe the whole effort,
  // not one set of it. Same inference the backend makes in
  // session_capture_service.
  const isPerSetExercise = hasConfiguredFields
    ? false
    : (template?.per_set ?? (exerciseType === 'strength' || exerciseType === 'mix'))
  const isWeightBased = isWeightBasedModule(exerciseType)

  const initialSets = exercise.set_results && exercise.set_results.length > 0
    ? exercise.set_results
    : Array.from({ length: (exercise.warmup_sets || 0) + (exercise.working_sets || 3) }, (_, i) => ({
        set_number: i + 1,
        set_type: i < (exercise.warmup_sets || 0) ? 'warmup' : 'working',
        actual_reps: null,
        actual_weight: null,
        needs_confirmation: false
      }))

  const [sets, setSets] = useState(initialSets)
  // VH-206 BUG-3: the machine answer, asked ONLY on the server's say-so
  // (needs_equipment_choice) — never derived from the candidate count here.
  // Pre-answered with the location variant's remembered machine, as the
  // capture sheet does.
  const [machineId, setMachineId] = useState(
    () =>
      exercise.performed_equipment_id ??
      (exercise.needs_equipment_choice ? exercise.variant?.suggested_equipment_id : null) ??
      null
  )
  // VH-206 BUG-9: correcting a machine already recorded.
  const [correctingMachine, setCorrectingMachine] = useState(false)
  const [_isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (exercise.set_results && exercise.set_results.length > 0) {
      setSets(exercise.set_results)
    }
  }, [exercise.set_results])

  const [metricValues, setMetricValues] = useState(() => {
    const existingMetrics = exercise.metric_values || {}
    const fields = resolvedFields
    const values = {}
    fields.forEach(field => {
      values[field.key] = existingMetrics[field.key] ||
        exercise[`actual_${field.key}`] ||
        (field.key === 'duration' ? (exercise.actual_duration || '') : '') ||
        (field.key === 'rounds' ? (exercise.actual_rounds || '') : '') ||
        (field.key === 'distance' ? (exercise.actual_distance || '') : '') ||
        (field.key === 'intensity' ? (exercise.actual_intensity || '') : '') ||
        ''
    })
    return values
  })

  useEffect(() => {
    if (exercise.metric_values) {
      const existingMetrics = exercise.metric_values
      const fields = resolvedFields
      const values = {}
      fields.forEach(field => {
        values[field.key] = existingMetrics[field.key] ?? ''
      })
      setMetricValues(values)
    }
    // Safe to depend on the array itself only because useFieldTemplates now
    // memoizes its lookup (VH-153). Before that, `getTemplate()` returned a new
    // object every render and this effect re-fired forever: it set state, which
    // re-rendered, which rebuilt the template. That hung a test run for 300s.
  }, [exercise.metric_values, resolvedFields])

  const { data: pbData } = useQuery({
    queryKey: ['personalBest', exercise.source_exercise_id, exercise.snapshot_exercise_name_en],
    queryFn: () => weeklyProgramsAPI.getPersonalBest(exercise.source_exercise_id || 0, exercise.snapshot_exercise_name_en),
    enabled: !!exercise.snapshot_exercise_name_en && isWeightBased
  })

  const personalBest = pbData?.data

  const updateSetsMutation = useMutation({
    mutationFn: (data) => weeklyProgramsAPI.updateExerciseSets(exercise.id, data),
    onSuccess: (response) => {
      const validation = response?.data?.validation

      if (validation && validation.warnings && validation.warnings.length > 0) {
        toast(
          (_tst) => (
            <div>
              <p className="font-medium">{t('programs.resultsSavedWithWarnings')}</p>
              <ul className="text-sm mt-1 list-disc pl-4">
                {validation.warnings.map((w, i) => (
                  <li key={i} className="text-orange-600">{w.message}</li>
                ))}
              </ul>
            </div>
          ),
          { duration: 8000, icon: '⚠️' }
        )
      } else {
        toast.success(t('programs.resultsSaved'))
      }

      setIsEditing(false)
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
    },
    onError: (error) => {
      console.error('Failed to save results:', error)
      toast.error(errorDetail(error) || t('programs.resultsSaveFailed'))
    }
  })

  // VH-206 BUG-9: the same PUT the machine review uses; the server rejects a
  // non-candidate with 422, whose detail is shown.
  const correctMachineMutation = useMutation({
    mutationFn: (equipmentId) =>
      weeklyProgramsAPI.updateExercise(exercise.id, { performed_equipment_id: equipmentId }),
    onSuccess: () => {
      setCorrectingMachine(false)
      toast.success(t('machineChoice.corrected'))
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
    },
    onError: (error) => {
      console.error('Failed to correct machine:', error)
      toast.error(errorDetail(error) || t('machineChoice.correctFailed'))
    }
  })

  // Its own mutation so the accept button can show its own pending state and
  // its own toast without borrowing the Save button's wording.
  const acceptSetMutation = useMutation({
    mutationFn: (data) => weeklyProgramsAPI.updateExerciseSets(exercise.id, data),
    onSuccess: () => {
      toast.success(t('programs.unconfirmed.accepted'))
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      queryClient.invalidateQueries({ queryKey: ['personalBest'] })
    },
    onError: (error) => {
      console.error('Failed to confirm set value:', error)
      toast.error(t('programs.unconfirmed.acceptFailed'))
    }
  })

  const skipMutation = useMutation({
    mutationFn: () => exercise.is_skipped
      ? weeklyProgramsAPI.unskipExercise(exercise.id)
      : weeklyProgramsAPI.skipExercise(exercise.id, { reason: null }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['programDetail', program.id] })
      await queryClient.refetchQueries({ queryKey: ['programDetail', program.id] })
      toast.success(exercise.is_skipped ? t('programs.exerciseRestored') : t('programs.exerciseSkipped'))
    },
    onError: (error) => {
      console.error('Failed to skip/unskip exercise:', error)
      toast.error(t('programs.skipFailed'))
    }
  })

  // VH-208: editing the REPS of a value the photo reader flagged IS the
  // correction — the flag clears with the edit and is persisted by the same
  // Save that persists the number. Editing the weight does not clear it: the
  // guard is about the rep count, and clearing on any touch would let an
  // unrelated edit launder the flag.
  const handleSetChange = (index, field, value) => {
    const newSets = [...sets]
    newSets[index] = {
      ...newSets[index],
      [field]: value === '' ? null : Number(value),
      ...(field === 'actual_reps' && newSets[index]?.needs_confirmation
        ? { needs_confirmation: false }
        : {})
    }
    setSets(newSets)
  }

  // VH-208: accepting is the same value written back with the flag cleared —
  // the existing set update, not a second way to write a set (VH-139).
  const acceptSet = (index) => {
    const accepted = sets.map((s, i) =>
      i === index ? { ...s, needs_confirmation: false } : s
    )
    setSets(accepted)
    acceptSetMutation.mutate({ sets: accepted })
  }

  const setMetricValue = useCallback((key, value) => {
    setMetricValues(prev => ({ ...prev, [key]: value }))
  }, [])

  // Propose avg. speed from distance + duration (bike/e-bike/cardio …); the
  // athlete can overwrite it and we then stay out of the way.
  const { speedKey, isProposed, handleChange: handleMetricChange } = useAvgSpeedProposal({
    fields: resolvedFields,
    values: metricValues,
    onChange: setMetricValue,
    enabled: isEditable,
  })

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const form = e.target.form || e.target.closest('.space-y-2, .space-y-3')
      if (form) {
        const inputs = Array.from(form.querySelectorAll('input[type="number"], input[type="text"]'))
        const currentIndex = inputs.indexOf(e.target)
        if (currentIndex < inputs.length - 1) {
          inputs[currentIndex + 1].focus()
          inputs[currentIndex + 1].select()
        }
      }
    }
  }

  const handleSave = () => {
    if (onSaveSession && sessionData) {
      // VH-124 Fix 4: persist the session date/notes silently — the set/metric
      // save below shows the single "results saved" toast, so one Save = one toast.
      onSaveSession({ silent: true })
    }

    // VH-206: the machine goes out ONLY when the card asked and the athlete
    // answered. Unasked -> the server infers; skipped -> nothing recorded.
    const machineField =
      exercise.needs_equipment_choice && machineId != null
        ? { performed_equipment_id: machineId }
        : {}

    if (isPerSetExercise) {
      updateSetsMutation.mutate({ sets, ...machineField })
    } else {
      const cleanedMetrics = {}
      Object.entries(metricValues).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          cleanedMetrics[key] = typeof value === 'string' ? parseFloat(value) || value : value
        }
      })
      updateSetsMutation.mutate({ metric_values: cleanedMetrics, ...machineField })
    }
  }

  // "Logged" means the athlete SAVED results, which only `captured_at` can tell
  // us. Set rows and metric_values both arrive PRE-FILLED from the best previous
  // session as targets to beat, so inferring from their values marked every
  // exercise in an untouched session as logged before anything was entered.
  const hasResults = exercise.captured_at != null

  const getTypeStyle = () => {
    switch (exerciseType) {
      case 'cardio':
      case 'running':
      case 'cycling':
        return { bg: 'bg-blue-100', text: 'text-blue-700', icon: '🚴' }
      case 'tabata':
      case 'hiit':
        return { bg: 'bg-orange-100', text: 'text-orange-700', icon: '⏱️' }
      case 'group_class':
        return { bg: 'bg-purple-100', text: 'text-purple-700', icon: '👥' }
      case 'recovery':
        return { bg: 'bg-green-100', text: 'text-green-700', icon: '🧘' }
      default:
        return { bg: 'bg-red-100', text: 'text-red-700', icon: '🏋️' }
    }
  }

  const typeStyle = getTypeStyle()

  // VH-153: the exercise's OWN configured fields win over the generic per-type
  // template — the rule VH-139 established for the capture sheet, which this
  // card never received. Reto's "Walking / Hiking" is configured with duration,
  // distance, intensity and steps; the cardio template defines exactly duration
  // and distance, so the card showed two of his four fields and no way to reach
  // the others.
  //
  // Not merged with the template fields: VH-139 found merging shows the same
  // quantity twice under two names (`duration` beside `duration_min`).
  //
  // Falls back to the template when the snapshot is absent, which keeps every
  // pre-VH-153 session and every unconfigured exercise rendering as before.
  const templateFields = resolvedFields

  return (
    <div className={`border rounded-lg overflow-hidden ${
      exercise.is_skipped
        ? 'border-gray-300 bg-gray-100 opacity-60'
        : hasResults
          ? 'border-green-300 bg-green-50/30'
          : 'border-gray-200 bg-surface-container'
    }`}>
      {/* Card Header */}
      <div className={`px-4 py-3 border-b flex justify-between items-start ${
        exercise.is_skipped ? 'bg-gray-200 border-gray-300' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeStyle.bg} ${typeStyle.text}`}>
              {typeStyle.icon} {exerciseType.replace('_', ' ')}
            </span>
            {exercise.is_skipped && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-300 text-gray-700">
                {t('programs.skipped')}
              </span>
            )}
            {exercise.snapshot_muscle_group && (
              <span className="text-xs text-gray-500">
                {t(`exercises.muscleGroups.${exercise.snapshot_muscle_group}`, { defaultValue: exercise.snapshot_muscle_group })}
              </span>
            )}
          </div>
          <h4 className={`font-semibold mt-1 ${exercise.is_skipped ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
            {exercise.snapshot_exercise_name_en}
          </h4>
          {exercise.snapshot_equipment_name_en && (
            <p className="text-xs text-gray-500">{exercise.snapshot_equipment_name_en}</p>
          )}
          {/* VH-206: what it was actually LIFTED on, shown only once the
              athlete has logged it — a planned exercise has no machine yet.
              Absent means nobody said, and says so rather than implying one. */}
          {exercise.captured_at != null && !exercise.is_skipped && (
            <p className="text-xs text-gray-500" data-testid={`performed-machine-${exercise.id}`}>
              {/* VH-206 BUG-7: a recorded machine that has since been removed
                  is not shown as a normal machine. */}
              {exercise.performed_equipment_unavailable
                ? t('machineChoice.unavailable', { name: exercise.performed_equipment_name })
                : exercise.performed_equipment_name
                  ? t('machineChoice.performedOn', { name: exercise.performed_equipment_name })
                  : t('machineChoice.notRecorded')}
              {(exercise.equipment_candidates || []).length > 0 && (
                <button
                  type="button"
                  onClick={() => setCorrectingMachine((v) => !v)}
                  data-testid={`performed-machine-correct-${exercise.id}`}
                  className="ml-2 text-primary-600 hover:text-primary-700 underline"
                >
                  {correctingMachine ? t('common.cancel', 'Cancel') : t('machineChoice.correct')}
                </button>
              )}
            </p>
          )}
          {exercise.exercise_notes && (
            <p className="text-xs text-gray-500 mt-1 italic">{exercise.exercise_notes}</p>
          )}
          {exercise.skip_reason && (
            <p className="text-xs text-gray-600 mt-1">{t('programs.skipReason')}: {exercise.skip_reason}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {exercise.is_unplanned && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
              {t('programs.unplanned')}
            </span>
          )}
          {hasResults && !exercise.is_skipped && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
              <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
              {t('programs.logged')}
            </span>
          )}
        </div>
      </div>

      {/* VH-206 BUG-3: the same question the capture sheet asks, on the
          server's say-so only. Hidden once correcting, which reuses it. */}
      {exercise.needs_equipment_choice && isEditable && !exercise.is_skipped && !correctingMachine && (
        <MachineChoice
          candidates={exercise.equipment_candidates || []}
          value={machineId}
          onChange={setMachineId}
          testIdPrefix={`card-machine-${exercise.id}`}
        />
      )}
      {/* VH-206 BUG-9: correct a machine already recorded (inferred,
          backfilled or answered) — saved at once, one tap. */}
      {correctingMachine && (
        <MachineChoice
          candidates={exercise.equipment_candidates || []}
          value={exercise.performed_equipment_id ?? null}
          disabled={correctMachineMutation.isPending}
          onChange={(equipmentId) => {
            if (equipmentId != null && equipmentId !== exercise.performed_equipment_id) {
              correctMachineMutation.mutate(equipmentId)
            }
          }}
          testIdPrefix={`card-machine-correct-${exercise.id}`}
        />
      )}

      {/* Card Body */}
      <div className="px-4 py-3">
        {isPerSetExercise ? (
          <div>
            <div className="flex items-center gap-4 mb-3 text-sm">
              {(exercise.working_sets || exercise.target_reps_min) && (
                <div className="text-gray-600">
                  <span className="font-medium">{t('trainingDays.target')}:</span>{' '}
                  {exercise.warmup_sets > 0 && <span className="text-gray-400">{exercise.warmup_sets}wu + </span>}
                  {exercise.working_sets} × {exercise.target_reps_min}
                  {exercise.target_reps_max && exercise.target_reps_max !== exercise.target_reps_min && `-${exercise.target_reps_max}`}
                </div>
              )}
              {personalBest && personalBest.max_weight && (
                <div className="inline-flex items-center px-2 py-0.5 bg-yellow-50 border border-yellow-200 rounded">
                  <TrophyIcon className="h-3.5 w-3.5 text-yellow-500 mr-1" />
                  <span className="text-xs font-medium text-yellow-700">
                    PB: {personalBest.max_weight}kg × {personalBest.max_weight_reps}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-500 px-2">
                <div className="w-16">{t('trainingDays.set')}</div>
                <div className="w-20 text-center">{t('trainingDays.weight')} <span className="text-red-500">*</span></div>
                <div className="w-4"></div>
                <div className="w-16 text-center">{t('trainingDays.reps')} <span className="text-red-500">*</span></div>
              </div>
              {(isEditable ? sets : (exercise.set_results || sets)).map((set, idx) => {
                // VH-208: the photo reader flagged this rep count as out of
                // line with its siblings. It is stored, not discarded — but it
                // counts towards nothing until the athlete says it is real.
                const isUnconfirmed = Boolean(set.needs_confirmation)
                // The stored row is still flagged but the edit state is not:
                // the athlete has corrected the reps and only Save is missing.
                // VH-207: the stored weight is an aim from the location
                // variant, not yet lifted here. Read from the STORED row, so
                // the mark stays until a save clears it server-side.
                const isTarget = Boolean(exercise.set_results?.[idx]?.is_target)
                const willConfirmOnSave =
                  isEditable &&
                  !isUnconfirmed &&
                  Boolean(exercise.set_results?.[idx]?.needs_confirmation)
                return (
                <div
                  key={idx}
                  data-testid={`exercise-set-row-${idx}${isUnconfirmed ? '-unconfirmed' : ''}`}
                  className={`flex flex-wrap items-center gap-2 p-2 rounded border ${
                    isUnconfirmed
                      ? 'bg-amber-50 border-l-4 border-l-amber-500 border-amber-300'
                      : set.set_type === 'warmup'
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-surface-container border-gray-300'
                  }`}
                >
                  <div className="w-16 text-sm font-medium text-gray-600">
                    {set.set_type === 'warmup' ? `Warmup ${idx + 1}` : `Set ${idx - (exercise.warmup_sets || 0) + 1}`}
                  </div>
                  {isEditable ? (
                    <>
                      <input
                        type="number"
                        value={set.actual_weight ?? ''}
                        onChange={(e) => handleSetChange(idx, 'actual_weight', e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-20 text-center text-sm focus:border-primary-500 focus:ring-primary-500 py-1"
                        placeholder="kg"
                        min="0"
                        step="0.5"
                      />
                      <span className="text-gray-400 w-4 text-center">×</span>
                      <input
                        type="number"
                        value={set.actual_reps ?? ''}
                        onChange={(e) => handleSetChange(idx, 'actual_reps', e.target.value)}
                        onKeyDown={handleKeyDown}
                        aria-label={isUnconfirmed ? t('programs.unconfirmed.correctHint') : undefined}
                        className={`w-16 text-center text-sm focus:border-primary-500 focus:ring-primary-500 py-1 ${
                          isUnconfirmed ? 'border-amber-400 bg-surface-container font-semibold text-amber-900' : ''
                        }`}
                        placeholder="reps"
                        min="0"
                      />
                    </>
                  ) : (
                    <div className={`text-sm font-medium ${isUnconfirmed ? 'text-amber-900' : 'text-gray-900'}`}>
                      {set.actual_weight ?? '-'} kg × {set.actual_reps ?? '-'}
                    </div>
                  )}

                  {isTarget && (
                    <span
                      data-testid={`exercise-set-target-${idx}`}
                      className="rounded border border-dashed border-primary-400 px-2 py-0.5 text-xs font-semibold italic text-primary-700"
                      title={t('variants.targetExplain')}
                    >
                      {t('variants.target')}
                    </span>
                  )}

                  {isUnconfirmed && (
                    <>
                      <span
                        className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
                        title={t('programs.unconfirmed.excluded')}
                      >
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                        {t('programs.unconfirmed.badge')}
                      </span>
                      {isEditable && (
                        <button
                          type="button"
                          onClick={() => acceptSet(idx)}
                          disabled={acceptSetMutation.isPending}
                          className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          {acceptSetMutation.isPending
                            ? t('programs.unconfirmed.accepting')
                            : t('programs.unconfirmed.accept')}
                        </button>
                      )}
                      <p className="w-full text-xs text-amber-800">
                        {t('programs.unconfirmed.explain')}
                      </p>
                    </>
                  )}

                  {willConfirmOnSave && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                      <CheckCircleIcon className="h-3.5 w-3.5" />
                      {t('programs.unconfirmed.willConfirm')}
                    </span>
                  )}
                </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {templateFields.map((field) => {
              const isRequired = field.required !== false
              return (
                <div key={field.key}>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {getFieldLabel(field, i18n.language)}
                    {field.unit && <span className="text-gray-400 ml-1">({field.unit})</span>}
                    {isRequired && <span className="text-red-500 ml-0.5">*</span>}
                    {field.key === speedKey && isProposed && (
                      <span className="text-gray-400 ml-1 font-normal">
                        {t('capture.calculatedHint', 'calculated — edit to override')}
                      </span>
                    )}
                  </label>
                  {isEditable ? (
                    <input
                      type={field.type === 'number' ? 'number' : 'text'}
                      value={metricValues[field.key] ?? ''}
                      onChange={(e) => handleMetricChange(field.key, e.target.value)}
                      onKeyDown={handleKeyDown}
                      className={`w-full text-sm rounded focus:border-primary-500 focus:ring-primary-500 py-1.5 ${
                        isRequired ? 'border-gray-300' : 'border-gray-200'
                      }`}
                      placeholder={field.unit || '-'}
                      min={field.min}
                      max={field.max}
                      required={isRequired}
                    />
                  ) : (
                    <div className="text-sm font-medium text-gray-900">
                      {exercise.metric_values?.[field.key] || '-'} {field.unit || ''}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {isEditable && (
          <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap gap-2 justify-between items-center">
            <div className="flex gap-2">
              {exercise.is_skipped ? (
                <button
                  onClick={() => skipMutation.mutate()}
                  disabled={skipMutation.isPending}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-1"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  {t('programs.restoreExercise')}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => skipMutation.mutate()}
                    disabled={skipMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700 flex items-center gap-1"
                  >
                    <NoSymbolIcon className="h-4 w-4" />
                    {t('programs.skipExercise')}
                  </button>
                  <button
                    onClick={onSubstitute}
                    className="px-3 py-1.5 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-amber-100 hover:text-amber-700 flex items-center gap-1"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4" />
                    {t('programs.substituteExercise')}
                  </button>
                </>
              )}
            </div>
            {!exercise.is_skipped && (
              <button
                onClick={handleSave}
                disabled={updateSetsMutation.isPending}
                className="px-4 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-on-primary hover:bg-primary-700 flex items-center gap-1"
              >
                <CheckCircleIcon className="h-4 w-4" />
                {updateSetsMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
